/**
 * Trunnion AI - Conditional Node
 * Branching logic node with multiple output handles.
 */

import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { GitBranch } from 'lucide-react';
import type { ConditionalNodeData } from '@/types';
import { useWorkflowDesignerStore } from '@/stores/workflowDesignerStore';

export const ConditionalNode = memo(function ConditionalNode({
  id,
  data,
  selected,
}: NodeProps & { data: ConditionalNodeData }) {
  const setSelectedNodeId = useWorkflowDesignerStore((s) => s.setSelectedNodeId);

  return (
    <div
      className={`min-w-50 rounded-xl border-2 bg-trunnion-slate-800 shadow-lg transition-all ${
        selected
          ? 'border-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.3)]'
          : 'border-yellow-500/40 hover:border-yellow-500/60'
      }`}
      onClick={() => setSelectedNodeId(id)}
    >
      <div className="flex items-center gap-2 rounded-t-[10px] border-b border-trunnion-slate-700 bg-yellow-500/10 px-3 py-2">
        <GitBranch className="h-4 w-4 text-yellow-400" />
        <span className="text-sm font-semibold text-white">
          {data.label || 'Condition'}
        </span>
      </div>

      <div className="px-3 py-2.5 space-y-1.5">
        <p className="text-xs text-trunnion-slate-400 uppercase tracking-wider">
          {data.conditionType}
        </p>
        {data.condition && (
          <p className="text-xs text-trunnion-slate-300 font-mono truncate">
            {data.condition}
          </p>
        )}
        <div className="flex gap-2">
          {data.branches.map((branch, i) => (
            <span
              key={i}
              className="rounded bg-yellow-500/10 px-1.5 py-0.5 text-[10px] text-yellow-300"
            >
              {branch.label}
            </span>
          ))}
        </div>
      </div>

      <Handle
        type="target"
        position={Position.Top}
        className="!bg-trunnion-slate-800 !border-yellow-500"
      />
      {/* Multiple source handles for branches */}
      {data.branches.map((branch, i) => (
        <Handle
          key={branch.targetHandleId}
          type="source"
          position={Position.Bottom}
          id={branch.targetHandleId}
          className="!bg-trunnion-slate-800 !border-yellow-500"
          style={{
            left: `${((i + 1) / (data.branches.length + 1)) * 100}%`,
          }}
        />
      ))}
    </div>
  );
});
